import {
  faArrowLeft,
  faCircleCheck,
  faCircleExclamation,
  faCircleInfo,
  faDownload,
  faTrash,
  faUpload,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
// @ts-ignore — FileUploaderItem is exported at runtime but missing from @wfp/ui's index.d.ts
import { Button, FileUploaderItem, Loading } from "@wfp/ui"
import useCommon from "hooks/useCommon"
import { useMemo, useRef, useState } from "react"
import "react-dates/initialize"
import { useNavigate } from "react-router-dom"
import Select from "react-select"
import {
  UploadBatchWarning,
  UploadInvalidRow,
  UploadResponse,
  UploadStructuralErrorResponse,
  UploadWarnedRow,
  useDeleteStagedRowsMutation,
  useDownloadAssetsTemplateMutation,
  useDownloadStagedRowsMutation,
  useListCountriesQuery,
  useUploadAssetsMutation,
} from "services/aimsBackend"

type Option = { value: string; label: string }

// ────────────────────────────────────────────────────────────────────
// Inline styles (declared up-front so sub-components can reference them)
// ────────────────────────────────────────────────────────────────────

const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "8px 12px",
  fontSize: 12,
  textTransform: "uppercase",
  letterSpacing: 0.4,
  color: "#5a6872",
  borderBottom: "1px solid #e8e8e8",
}

const tdStyle: React.CSSProperties = {
  padding: "8px 12px",
  borderBottom: "1px solid #f0f0f0",
}

const trStyle: React.CSSProperties = {}

const errorChipStyle: React.CSSProperties = {
  background: "#fde8e8",
  color: "#c62828",
  padding: "2px 8px",
  borderRadius: 12,
  fontSize: 12,
  fontWeight: 600,
  display: "inline-block",
}

const warningChipStyle: React.CSSProperties = {
  background: "#fff4e0",
  color: "#b86200",
  padding: "2px 8px",
  borderRadius: 12,
  fontSize: 12,
  fontWeight: 600,
  display: "inline-block",
}

const panelStyle: React.CSSProperties = {
  background: "#fafafa",
  border: "1px solid #e8e8e8",
  borderRadius: 6,
  padding: 16,
  marginBottom: 24,
}

// ────────────────────────────────────────────────────────────────────
// Helpers — friendly labels for backend codes
// ────────────────────────────────────────────────────────────────────

const ERROR_CODE_LABELS: Record<string, string> = {
  required: "Required",
  null: "Missing value",
  blank: "Empty value",
  invalid: "Invalid value",
  invalid_choice: "Not in allowed values",
  invalid_format: "Wrong format",
  invalid_date_range: "End date is before start date",
  invalid_asset_type: "Unknown asset type",
  invalid_asset_subcat: "Subcategory does not match asset type",
  duplicate_asset: "Duplicate asset",
}

const WARNING_CODE_LABELS: Record<string, string> = {
  geotrace_empty: "LIA row missing geotrace",
  asset_age: "End year is too recent for analysis",
  ada_upload_count: "Many ADA rows in one upload",
  lia_upload_count: "Many LIA rows in one upload",
}

function labelForError(code: string): string {
  return ERROR_CODE_LABELS[code] ?? code.replace(/_/g, " ")
}

function labelForWarning(code: string): string {
  return WARNING_CODE_LABELS[code] ?? code.replace(/_/g, " ")
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined || v === "") return "—"
  if (typeof v === "object") return JSON.stringify(v)
  return String(v)
}

// ────────────────────────────────────────────────────────────────────
// Result lane discriminator
// ────────────────────────────────────────────────────────────────────

type ResultLane =
  | { kind: "valid"; response: Extract<UploadResponse, { status: "valid" }> }
  | { kind: "row_failures"; response: Extract<UploadResponse, { detail: { invalid_rows: unknown[] } }> }
  | {
      kind: "structural"
      response: UploadStructuralErrorResponse
      subKind: "column_count" | "column_names" | "row_count"
    }
  | { kind: "file_error"; messages: string[] }
  | { kind: "unknown"; raw: unknown }

function classifyResponse(payload: any): ResultLane {
  if (!payload || typeof payload !== "object") return { kind: "unknown", raw: payload }
  const status = payload.status

  // 201 — all rows valid
  if (status === "valid" && payload.detail) {
    return { kind: "valid", response: payload }
  }

  // 200 partial / 422 all-invalid (both have batch_id + invalid_rows)
  if ((status === "partial" || status === "error") && payload.batch_id && payload.detail?.invalid_rows !== undefined) {
    return { kind: "row_failures", response: payload }
  }

  // 422 structural — pick subKind from error_summary
  if (status === "error" && payload.error_summary && payload.detail) {
    const keys = Object.keys(payload.error_summary)
    if (keys.includes("column_count")) return { kind: "structural", response: payload, subKind: "column_count" }
    if (keys.includes("column_names")) return { kind: "structural", response: payload, subKind: "column_names" }
    if (keys.includes("row_count")) return { kind: "structural", response: payload, subKind: "row_count" }
  }

  // 400 / file_error
  if (status === "error" && payload.error_summary) {
    const messages: string[] = []
    Object.entries(payload.error_summary as Record<string, string[]>).forEach(([key, msgs]) => {
      ;(msgs || []).forEach(m => messages.push(`${key}: ${m}`))
    })
    return { kind: "file_error", messages }
  }

  return { kind: "unknown", raw: payload }
}

// ────────────────────────────────────────────────────────────────────
// Sub-panels
// ────────────────────────────────────────────────────────────────────

function StatusBanner({
  tone,
  icon,
  title,
  subtitle,
}: {
  tone: "success" | "warning" | "error"
  icon: any
  title: string
  subtitle?: string
}) {
  const palette =
    tone === "success"
      ? { bg: "#e6f6ed", border: "#1f8a4c", color: "#1f8a4c" }
      : tone === "warning"
      ? { bg: "#fff4e0", border: "#b86200", color: "#b86200" }
      : { bg: "#fde8e8", border: "#c62828", color: "#c62828" }
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        background: palette.bg,
        border: `1px solid ${palette.border}`,
        borderRadius: 6,
        padding: "12px 16px",
        marginBottom: 16,
      }}
    >
      <FontAwesomeIcon icon={icon} style={{ color: palette.color, fontSize: 20 }} />
      <div>
        <p className="m-0" style={{ fontWeight: 700, fontSize: 15, color: palette.color }}>
          {title}
        </p>
        {subtitle && (
          <p className="m-0" style={{ fontSize: 13, color: "#3a3a3a", marginTop: 2 }}>
            {subtitle}
          </p>
        )}
      </div>
    </div>
  )
}

function StatTiles({
  tiles,
}: {
  tiles: Array<{ label: string; value: string | number; tone?: "success" | "warning" | "error" }>
}) {
  if (!tiles.length) return null
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
        gap: 12,
        marginBottom: 16,
      }}
    >
      {tiles.map(t => (
        <div key={t.label} style={{ background: "#f5f7fa", padding: 14, borderRadius: 6 }}>
          <p className="m-0" style={{ fontSize: 12, color: "#5a6872", textTransform: "uppercase", letterSpacing: 0.4 }}>
            {t.label}
          </p>
          <p
            className="m-0"
            style={{
              fontSize: 24,
              fontWeight: 700,
              color:
                t.tone === "error"
                  ? "#c62828"
                  : t.tone === "warning"
                  ? "#b86200"
                  : t.tone === "success"
                  ? "#1f8a4c"
                  : "#1a1a1a",
              marginTop: 4,
            }}
          >
            {t.value}
          </p>
        </div>
      ))}
    </div>
  )
}

function SectionHeading({ children, count }: { children: React.ReactNode; count?: number }) {
  return (
    <h3 className="m-0" style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>
      {children}
      {count !== undefined && <span style={{ fontWeight: 400, color: "#5a6872", marginLeft: 8 }}>({count})</span>}
    </h3>
  )
}

function InvalidRowsTable({ rows }: { rows: UploadInvalidRow[] }) {
  if (!rows.length) return null
  return (
    <div style={{ marginBottom: 24 }}>
      <SectionHeading count={rows.length}>Rows with errors</SectionHeading>
      <div style={{ overflow: "auto", border: "1px solid #e8e8e8", borderRadius: 6 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead style={{ background: "#f5f7fa" }}>
            <tr>
              <th style={thStyle}>Row</th>
              <th style={thStyle}>Field</th>
              <th style={thStyle}>Value</th>
              <th style={thStyle}>Issue</th>
            </tr>
          </thead>
          <tbody>
            {rows.flatMap(row =>
              row.errors.map((err, i) => (
                <tr key={`${row.row}-${err.field}-${i}`} style={trStyle}>
                  {i === 0 && (
                    <td rowSpan={row.errors.length} style={{ ...tdStyle, fontWeight: 700, verticalAlign: "top" }}>
                      {row.row}
                    </td>
                  )}
                  <td style={tdStyle}>{err.field}</td>
                  <td style={{ ...tdStyle, color: "#5a6872" }}>{formatValue(err.value)}</td>
                  <td style={{ ...tdStyle, color: "#c62828" }}>
                    <span style={errorChipStyle}>{labelForError(err.error)}</span>
                  </td>
                </tr>
              )),
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function WarnedRowsTable({ rows }: { rows: UploadWarnedRow[] }) {
  if (!rows.length) return null
  return (
    <div style={{ marginBottom: 24 }}>
      <SectionHeading count={rows.length}>Rows with warnings</SectionHeading>
      <div style={{ overflow: "auto", border: "1px solid #e8e8e8", borderRadius: 6 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead style={{ background: "#f5f7fa" }}>
            <tr>
              <th style={thStyle}>Row</th>
              <th style={thStyle}>Field</th>
              <th style={thStyle}>Value</th>
              <th style={thStyle}>Warning</th>
            </tr>
          </thead>
          <tbody>
            {rows.flatMap(row =>
              row.warnings.map((w, i) => (
                <tr key={`${row.row}-${w.field}-${i}`} style={trStyle}>
                  {i === 0 && (
                    <td rowSpan={row.warnings.length} style={{ ...tdStyle, fontWeight: 700, verticalAlign: "top" }}>
                      {row.row}
                    </td>
                  )}
                  <td style={tdStyle}>{w.field}</td>
                  <td style={{ ...tdStyle, color: "#5a6872" }}>{formatValue(w.value)}</td>
                  <td style={{ ...tdStyle, color: "#b86200" }}>
                    <span style={warningChipStyle}>{labelForWarning(w.warning)}</span>
                  </td>
                </tr>
              )),
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function BatchWarningsList({ warnings }: { warnings: UploadBatchWarning[] }) {
  if (!warnings.length) return null
  return (
    <div style={{ marginBottom: 24 }}>
      <SectionHeading count={warnings.length}>Batch warnings</SectionHeading>
      <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 8 }}>
        {warnings.map((w, i) => (
          <li
            key={i}
            style={{
              background: "#fff4e0",
              border: "1px solid #b86200",
              borderRadius: 6,
              padding: "10px 12px",
              fontSize: 13,
            }}
          >
            <p className="m-0" style={{ fontWeight: 700, color: "#b86200" }}>
              {labelForWarning(w.warning)}
            </p>
            <p className="m-0" style={{ marginTop: 2 }}>
              {w.detail}
            </p>
          </li>
        ))}
      </ul>
    </div>
  )
}

function StructuralErrorPanel({ lane }: { lane: Extract<ResultLane, { kind: "structural" }> }) {
  const { response, subKind } = lane
  const detail = response.detail as any

  if (subKind === "column_count") {
    return (
      <div style={panelStyle}>
        <SectionHeading>Wrong column count</SectionHeading>
        <p className="m-0" style={{ marginBottom: 8 }}>
          The file must contain exactly <b>{detail.expected_columns}</b> columns.
        </p>
        <p className="m-0">
          Your file has <b style={{ color: "#c62828" }}>{detail.actual_columns}</b> columns. Re-download the template to
          start from the correct headers.
        </p>
      </div>
    )
  }

  if (subKind === "column_names") {
    return (
      <div style={panelStyle}>
        <SectionHeading>Column names do not match the template</SectionHeading>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16 }}>
          <div>
            <p className="m-0" style={{ fontWeight: 700, fontSize: 13, marginBottom: 6, color: "#c62828" }}>
              Missing ({detail.missing.length})
            </p>
            {detail.missing.length === 0 ? (
              <p className="m-0" style={{ color: "#5a6872", fontSize: 13 }}>
                None
              </p>
            ) : (
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
                {detail.missing.map((c: string) => (
                  <li key={c}>{c}</li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <p className="m-0" style={{ fontWeight: 700, fontSize: 13, marginBottom: 6, color: "#b86200" }}>
              Unexpected ({detail.unexpected.length})
            </p>
            {detail.unexpected.length === 0 ? (
              <p className="m-0" style={{ color: "#5a6872", fontSize: 13 }}>
                None
              </p>
            ) : (
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
                {detail.unexpected.map((c: string) => (
                  <li key={c}>{c}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    )
  }

  // row_count
  return (
    <div style={panelStyle}>
      <SectionHeading>Too many rows</SectionHeading>
      <p className="m-0">
        The file has <b style={{ color: "#c62828" }}>{detail.actual_rows}</b> rows; the upload limit is{" "}
        <b>{detail.max_rows}</b>. Split the file and upload in batches.
      </p>
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────
// Result panel (top level)
// ────────────────────────────────────────────────────────────────────

function ResultPanel({
  lane,
  onUploadAnother,
  onDiscardBatch,
  onDownloadBatch,
  isDiscarding,
  isDownloadingBatch,
}: {
  lane: ResultLane
  onUploadAnother: () => void
  onDiscardBatch: () => void
  onDownloadBatch: (batchId: string) => void
  isDiscarding: boolean
  isDownloadingBatch: boolean
}) {
  const FooterActions = ({ batchId }: { batchId?: string }) => (
    <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
      {batchId && (
        <>
          <Button kind="secondary" onClick={() => onDownloadBatch(batchId)} disabled={isDownloadingBatch}>
            <FontAwesomeIcon icon={faDownload} className="mr-1" />
            {isDownloadingBatch ? "Downloading..." : "Download rows with errors"}
          </Button>
          <Button kind="danger" onClick={onDiscardBatch} disabled={isDiscarding}>
            <FontAwesomeIcon icon={faTrash} className="mr-1" />
            {isDiscarding ? "Discarding..." : "Discard staged batch"}
          </Button>
        </>
      )}
      <Button onClick={onUploadAnother}>
        <FontAwesomeIcon icon={faArrowLeft} className="mr-1" />
        Upload another file
      </Button>
    </div>
  )

  if (lane.kind === "valid") {
    const { detail } = lane.response
    const rowWarningCount = detail.warned_rows.reduce((count, row) => count + row.warnings.length, 0)
    const totalWarningCount = rowWarningCount + detail.batch_warnings.length
    const hasWarnings = totalWarningCount > 0
    return (
      <div>
        <StatusBanner
          tone={hasWarnings ? "warning" : "success"}
          icon={hasWarnings ? faCircleExclamation : faCircleCheck}
          title={hasWarnings ? "Assets uploaded with warnings - action required" : "All rows uploaded successfully"}
          subtitle={
            hasWarnings
              ? `${detail.assets_created} asset${
                  detail.assets_created === 1 ? "" : "s"
                } created, but ${totalWarningCount} warning${totalWarningCount === 1 ? "" : "s"} require review.`
              : `${detail.assets_created} asset${detail.assets_created === 1 ? "" : "s"} created from the file.`
          }
        />
        <StatTiles
          tiles={[
            { label: "Assets created", value: detail.assets_created, tone: "success" },
            { label: "Rows", value: detail.row_count },
            { label: "Columns", value: detail.column_count },
            ...(hasWarnings
              ? [
                  { label: "Rows with warnings", value: detail.warned_rows.length, tone: "warning" as const },
                  { label: "Total warnings", value: totalWarningCount, tone: "warning" as const },
                ]
              : []),
          ]}
        />
        <WarnedRowsTable rows={detail.warned_rows} />
        <BatchWarningsList warnings={detail.batch_warnings} />
        <FooterActions />
      </div>
    )
  }

  if (lane.kind === "row_failures") {
    const { detail, batch_id: batchId, status, error_summary: errorSummary } = lane.response
    const tone: "warning" | "error" = status === "partial" ? "warning" : "error"
    const title = status === "partial" ? "Some rows could not be uploaded" : "No rows could be uploaded"
    const subtitle = errorSummary?.field_validation?.[0]
    const totalInvalid = detail.invalid_rows.length
    return (
      <div>
        <StatusBanner tone={tone} icon={faCircleExclamation} title={title} subtitle={subtitle} />
        <StatTiles
          tiles={[
            {
              label: "Assets created",
              value: detail.assets_created,
              tone: detail.assets_created > 0 ? "success" : undefined,
            },
            { label: "Invalid rows", value: totalInvalid, tone: "error" },
            { label: "Warned rows", value: detail.warned_rows.length },
          ]}
        />
        <InvalidRowsTable rows={detail.invalid_rows} />
        <WarnedRowsTable rows={detail.warned_rows} />
        <BatchWarningsList warnings={detail.batch_warnings} />
        <FooterActions batchId={batchId} />
      </div>
    )
  }

  if (lane.kind === "structural") {
    return (
      <div>
        <StatusBanner
          tone="error"
          icon={faCircleExclamation}
          title="The file structure could not be processed"
          subtitle="No rows were uploaded. Fix the file structure and try again."
        />
        <StructuralErrorPanel lane={lane} />
        <FooterActions />
      </div>
    )
  }

  if (lane.kind === "file_error") {
    return (
      <div>
        <StatusBanner
          tone="error"
          icon={faCircleExclamation}
          title="The file could not be read"
          subtitle="Please make sure you upload a valid Excel (.xlsx) file."
        />
        <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 6 }}>
          {lane.messages.map((m, i) => (
            <li
              key={i}
              style={{
                background: "#fde8e8",
                border: "1px solid #c62828",
                borderRadius: 6,
                padding: "8px 12px",
                fontSize: 13,
                color: "#c62828",
              }}
            >
              {m}
            </li>
          ))}
        </ul>
        <FooterActions />
      </div>
    )
  }

  // unknown — last-resort fallback so we never silently swallow a response
  return (
    <div>
      <StatusBanner tone="error" icon={faCircleInfo} title="Unrecognised response" />
      <pre style={{ background: "#f5f7fa", padding: 12, borderRadius: 6, fontSize: 12, overflow: "auto" }}>
        {JSON.stringify(lane.raw, null, 2)}
      </pre>
      <FooterActions />
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────
// Page
// ────────────────────────────────────────────────────────────────────

export default function AddAsset() {
  const navigate = useNavigate()
  const { showSnackBar } = useCommon()

  const [country, setCountry] = useState<Option | null>(null)
  const [assetXlsx, setAssetXlsx] = useState<File | undefined>(undefined)
  const [assetZip, setAssetZip] = useState<File | undefined>(undefined)
  const [lane, setLane] = useState<ResultLane | undefined>(undefined)

  const xlsxInputRef = useRef<HTMLInputElement>(null)
  const zipInputRef = useRef<HTMLInputElement>(null)

  const [uploadAssets, { isLoading: isUploading }] = useUploadAssetsMutation()
  const [downloadTemplate, { isLoading: isDownloading }] = useDownloadAssetsTemplateMutation()
  const [downloadStagedRows, { isLoading: isDownloadingBatch }] = useDownloadStagedRowsMutation()
  const [deleteStagedRows, { isLoading: isDiscarding }] = useDeleteStagedRowsMutation()
  const { data: countriesList = [] } = useListCountriesQuery()

  const isLoading = isUploading || isDownloading || isDiscarding || isDownloadingBatch

  const geoOptions = useMemo<Option[]>(() => {
    return countriesList.map(item => ({ value: item.iso3, label: item.name }))
  }, [countriesList])

  const handleDownloadTemplate = async () => {
    try {
      const blob = await downloadTemplate().unwrap()
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = "aims-assets-template.xlsx"
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
    } catch {
      showSnackBar("Failed to download template", "error")
    }
  }

  const handleDownloadBatch = async (batchId: string) => {
    try {
      const blob = await downloadStagedRows({ batchId }).unwrap()
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = `aims-assets-errors-${batchId}.xlsx`
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
    } catch {
      showSnackBar("Failed to download rows with errors", "error")
    }
  }

  const handleUpload = async () => {
    if (!country) {
      showSnackBar("Please select a country or territory", "error")
      return
    }
    if (!assetXlsx) {
      showSnackBar("Please select the asset file (.xlsx)", "error")
      return
    }

    try {
      const response = await uploadAssets({ file: assetXlsx, country: country.value, kmzZip: assetZip }).unwrap()
      setLane(classifyResponse(response))
    } catch (err: any) {
      // 200/201 unwrap throws nothing; 4xx lands here with `err.data`
      const data = err?.data ?? { status: "error", error_summary: { file: ["Upload failed."] } }
      setLane(classifyResponse(data))
    }
  }

  const resetAll = () => {
    setLane(undefined)
    setAssetXlsx(undefined)
    setAssetZip(undefined)
  }

  const handleDiscardBatch = async () => {
    const batchId = lane && lane.kind === "row_failures" ? (lane.response.batch_id as string | undefined) : undefined
    if (!batchId) return
    try {
      await deleteStagedRows({ batchId }).unwrap()
      showSnackBar("Staged batch discarded", "success")
      resetAll()
    } catch {
      showSnackBar("Failed to discard staged batch", "error")
    }
  }

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 8, marginBottom: 16 }}>
        <Button
          kind="ghost"
          small
          onClick={() => navigate("/aims/AssetsView")}
          aria-label="Back to assets"
          style={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 0,
          }}
        >
          <FontAwesomeIcon icon={faArrowLeft} />
        </Button>
        <h2 className="m-0 mb-3 mt-2" style={{ marginTop: 0, marginBottom: 0 }}>
          Add assets from Excel file
        </h2>
      </div>

      <Loading active={isLoading} />

      {!lane && (
        <div>
          <div className="mb-3">
            <p className="m-0 mb-1" style={{ fontWeight: 600, fontSize: 14 }}>
              Country / Territory
            </p>
            <Select
              classNamePrefix="wfp--react-select"
              className="wfp--react-select-container"
              options={geoOptions}
              value={country}
              onChange={val => setCountry(val as Option | null)}
              placeholder="Select country or territory"
              isClearable
              noOptionsMessage={() => "No countries available yet"}
            />
          </div>

          <div className="mb-3">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
                marginBottom: 4,
                flexWrap: "wrap",
              }}
            >
              <p className="m-0" style={{ fontWeight: 600, fontSize: 14 }}>
                Asset file (.xlsx)
              </p>
              <Button kind="ghost" small onClick={handleDownloadTemplate} disabled={isDownloading}>
                <FontAwesomeIcon icon={faDownload} className="mr-1" />
                Download template
              </Button>
            </div>

            <input
              ref={xlsxInputRef}
              type="file"
              accept=".xlsx,.xls"
              style={{ display: "none" }}
              onChange={e => setAssetXlsx(e.target.files?.[0])}
            />

            {assetXlsx ? (
              <FileUploaderItem
                uuid={assetXlsx.name}
                name={assetXlsx.name}
                status="edit"
                iconDescription="Remove file"
                onDelete={() => {
                  setAssetXlsx(undefined)
                  if (xlsxInputRef.current) xlsxInputRef.current.value = ""
                }}
              />
            ) : (
              <Button kind="secondary" onClick={() => xlsxInputRef.current?.click()}>
                <FontAwesomeIcon icon={faUpload} className="mr-1" />
                Choose .xlsx file
              </Button>
            )}
          </div>

          <div className="mb-4">
            <p className="m-0 mb-1" style={{ fontWeight: 600, fontSize: 14 }}>
              Geographical file (.zip) <span style={{ fontWeight: 400, color: "#5a6872" }}>— optional</span>
            </p>

            <input
              ref={zipInputRef}
              type="file"
              accept=".zip"
              style={{ display: "none" }}
              onChange={e => setAssetZip(e.target.files?.[0])}
            />

            {assetZip ? (
              <FileUploaderItem
                uuid={assetZip.name}
                name={assetZip.name}
                status="edit"
                iconDescription="Remove file"
                onDelete={() => {
                  setAssetZip(undefined)
                  if (zipInputRef.current) zipInputRef.current.value = ""
                }}
              />
            ) : (
              <Button kind="secondary" onClick={() => zipInputRef.current?.click()}>
                <FontAwesomeIcon icon={faUpload} className="mr-1" />
                Choose .zip file
              </Button>
            )}
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button kind="ghost" onClick={() => navigate("/aims/AssetsView")} disabled={isUploading}>
              Cancel
            </Button>
            <Button onClick={handleUpload} disabled={isUploading || !assetXlsx || !country}>
              {isUploading ? "Uploading..." : "Upload"}
            </Button>
          </div>
        </div>
      )}

      {lane && (
        <ResultPanel
          lane={lane}
          onUploadAnother={resetAll}
          onDiscardBatch={handleDiscardBatch}
          onDownloadBatch={handleDownloadBatch}
          isDiscarding={isDiscarding}
          isDownloadingBatch={isDownloadingBatch}
        />
      )}
    </div>
  )
}
